import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { getCreatorCommunityToken } from "@/lib/creatorCommunityApi";
import { toast } from "sonner";
import { communitiesBase } from "@/lib/creator-community-embed";
import { useAuth, authClient } from "@/lib/auth";
import {
  ArrowLeft, Users, Settings, Send, Image as ImageIcon, Video as VideoIcon,
  Paperclip, Smile, X, Loader2, Lock, Pin, Trash2, Reply, Pencil, Play, Shield, Crown,
  MoreVertical, Radio, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RichText from "@/components/creator-community/RichText";
import FileUpload from "@/components/creator-community/FileUpload";
import MediaLightbox from "@/components/creator-community/MediaLightbox";
import VideoPopup from "@/components/creator-community/VideoPopup";
import { motion, AnimatePresence } from "framer-motion";
import PluseAdSense from "@/components/PluseAdSense";
import "./PluseAds.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMOJIS = ["👍", "❤️", "🔥", "😂", "😮", "😢", "🎉", "💯", "👀", "🙏"];
const PAGE_SIZE = 60;

function abs(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/files/")) {
    return `${BACKEND}/creator-api/files/${url.slice("/api/files/".length)}`;
  }
  return `${BACKEND}${url}`;
}
function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

function isOnlyEmojis(str) {
  if (!str) return false;
  const trimmed = str.trim();
  if (trimmed.length === 0) return false;
  const emojiRegex = /^(\p{Extended_Pictographic}|\p{Emoji_Component}|\s)+$/u;
  return emojiRegex.test(trimmed) && Array.from(trimmed).filter(c => c.trim() !== "").length <= 3;
}

function MediaGrid({ media, onImage, onVideo }) {
  if (!media?.length) return null;
  const images = media.filter((m) => m.kind === "image");
  return (
    <div className={`grid gap-1.5 mt-2 ${media.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
      {media.map((m, i) => {
        if (m.kind === "image") {
          const imgIdx = images.findIndex((x) => x === m);
          return (
            <img key={i} src={abs(m.url)} alt={m.name || ""} loading="lazy"
              onClick={() => onImage(images, imgIdx)}
              data-testid="message-image"
              className="rounded-xl object-cover w-full max-h-56 cursor-pointer hover:opacity-90 bg-black/30 transition-opacity" />
          );
        }
        if (m.kind === "video") {
          return (
            <button key={i} onClick={() => onVideo(m)} data-testid="message-video"
              className="relative rounded-xl overflow-hidden bg-black/50 aspect-video flex items-center justify-center group w-full">
              {m.thumbnail_url ? <img src={abs(m.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : <VideoIcon className="w-8 h-8 text-white/40" />}
              <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-colors">
                <span className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-md"><Play className="w-4 h-4 text-black ml-0.5" /></span>
              </span>
            </button>
          );
        }
        return (
          <a key={i} href={abs(m.url)} download target="_blank" rel="noreferrer" data-testid="message-file"
            className="flex items-center gap-2 p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-xs truncate max-w-full transition-colors">
            <Paperclip className="w-3.5 h-3.5 text-white/50 shrink-0" /><span className="truncate">{m.name || "File"}</span>
          </a>
        );
      })}
    </div>
  );
}

function MessageBubble({ m, me, perms, accentColor, communityId, replyTo, onReply, onPin, onDelete, onReact, onEdit, onImage, onVideo }) {
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.text || "");
  const [savingEdit, setSavingEdit] = useState(false);
  const saveEdit = async () => {
    const t = draft.trim();
    if (!t || t === m.text) { setEditing(false); return; }
    setSavingEdit(true);
    const ok = await onEdit(m, t);
    setSavingEdit(false);
    if (ok) setEditing(false);
  };
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showReactionDetails, setShowReactionDetails] = useState(false);
  const [reactionDetails, setReactionDetails] = useState(null);
  const [loadingReactionDetails, setLoadingReactionDetails] = useState(false);
  const mine = m.author_id === me?.id;
  const canDelete = mine || perms.includes("DELETE_MESSAGES");
  const canPin = perms.includes("PIN_MESSAGES");
  const reactions = Object.entries(m.reactions || {});
  const jumboEmoji = isOnlyEmojis(m.text);

  const loadReactionDetails = async () => {
    if (!reactions.length) return;

    // Open immediately so the gesture feels responsive.
    setShowReactionDetails(true);
    setLoadingReactionDetails(true);
    setReactionDetails(null);

    try {
      const { data } = await api.get(
        `/communities/${communityId}/messages/${m.id}/reactions`
      );

      console.log("Reaction details:", m.id, data);

      setReactionDetails(
        data && data.reactions && typeof data.reactions === "object"
          ? data.reactions
          : {}
      );
    } catch (e) {
      console.error(
        "Failed to load reaction details:",
        e.response?.data || e
      );
      setReactionDetails({});
    } finally {
      setLoadingReactionDetails(false);
    }
  };

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.05}
      onDragEnd={(e, info) => {
        const dx = info.offset.x;

        if (Math.abs(dx) < 60) return;

        // If a reaction panel is open, either direction closes it.
        if (showReactionDetails) {
          setShowReactionDetails(false);
          return;
        }

        // If this message is currently being replied to,
        // either direction closes the reply.
        if (replyTo?.id === m.id) {
          onReply(null);
          return;
        }

        // LEFT SWIPE = reactions
        if (dx < -60 && reactions.length > 0) {
          loadReactionDetails();
          return;
        }

        // RIGHT SWIPE = reply
        if (dx > 60) {
          onReply(m);
        }
      }}
      className={`flex gap-2.5 group relative max-w-full ${mine ? "flex-row-reverse" : ""}`}
      data-testid={`message-${m.id}`}
    >
      <Link to={`/u/${m.author?.username}`} className="shrink-0">
        <img src={m.author?.avatar_url || undefined} alt="" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-white/10 mt-1 shadow-sm" />
      </Link>
      
      <div className={`max-w-[82%] sm:max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col min-w-0 relative`}>
        <div 
          onDoubleClick={() => onReact(m.id, "❤️")}
          className={`rounded-[20px] px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-sm overflow-hidden break-words w-full transition-all ${
            jumboEmoji
              ? "bg-transparent border-none shadow-none px-1 py-1"
              : (mine ? "border" : "glass border border-white/10")
          }`}
          style={!jumboEmoji && mine ? {
            background: `${accentColor}18`,
            borderColor: `${accentColor}35`
          } : undefined}
        >
          {!jumboEmoji && (
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[11px] sm:text-xs font-semibold text-white/80 truncate">{m.author?.name || m.author?.username}</span>
              <span className="text-[9px] sm:text-[10px] text-white/35 shrink-0">{timeAgo(m.created_at)}</span>
              {m.edited && <span className="text-[9px] sm:text-[10px] text-white/35 italic shrink-0" data-testid={`msg-edited-${m.id}`}>(edited)</span>}
            </div>
          )}
          {m.reply_preview && !jumboEmoji && (
            <div className="mb-1.5 pl-2 border-l-2 border-white/20 text-[11px] sm:text-xs text-white/50 truncate">
              <span style={{ color: accentColor }}>@{m.reply_preview.author}</span>: {m.reply_preview.text}
            </div>
          )}
          {m.shared?.type === "live" && (
            <Link to={`/watch/${m.shared.video_id}`} className="block mb-2 rounded-xl overflow-hidden border border-red-500/30">
              <div className="relative aspect-video bg-black/50 flex items-center justify-center">
                {m.shared.thumbnail && <img src={m.shared.thumbnail} alt="" className="w-full h-full object-cover" />}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-[10px] font-bold"><Radio className="w-3 h-3" /> LIVE</span>
              </div>
              <div className="p-2 text-xs truncate">{m.shared.title}</div>
            </Link>
          )}
          {editing ? (
            <div className="flex flex-col gap-1.5 select-text" data-testid={`msg-edit-box-${m.id}`}>
              <textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === "Escape") { setEditing(false); setDraft(m.text || ""); }
                }}
                rows={Math.min(6, Math.max(1, draft.split("\n").length))}
                className="w-full min-w-[200px] bg-black/30 border border-white/15 rounded-xl px-2.5 py-1.5 text-sm text-white outline-none focus:border-white/40 resize-none"
                data-testid={`msg-edit-input-${m.id}`}
              />
              <div className="flex justify-end gap-1.5">
                <button onClick={() => { setEditing(false); setDraft(m.text || ""); }} className="px-2.5 py-1 rounded-full text-[11px] text-white/60 hover:bg-white/10" data-testid={`msg-edit-cancel-${m.id}`}>Cancel</button>
                <button onClick={saveEdit} disabled={savingEdit} className="px-3 py-1 rounded-full text-[11px] font-semibold text-black disabled:opacity-50" style={{ background: accentColor }} data-testid={`msg-edit-save-${m.id}`}>{savingEdit ? "Saving…" : "Save"}</button>
              </div>
            </div>
          ) : m.text && (
            <div className={jumboEmoji ? "text-8xl sm:text-9xl select-none leading-none py-2" : "text-xs sm:text-sm text-white/90 whitespace-pre-wrap break-words leading-relaxed"}>
              {m.text}
            </div>
          )}
          <MediaGrid media={m.media} onImage={onImage} onVideo={onVideo} />
        </div>

        {/* Reaction Quick Picker Popup */}
        <AnimatePresence>
          {showReactionPicker && (
            <motion.div 
              initial={{ scale: 0.8, opacity: 0, y: 5 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.8, opacity: 0, y: 5 }}
              className={`absolute z-[100] bottom-full mb-2 glass-strong rounded-full border border-white/20 px-3 py-1.5 flex gap-2 shadow-2xl ${mine ? "right-0" : "left-0"}`}
            >
              {EMOJIS.slice(0, 6).map((e) => (
                <button 
                  key={e} 
                  onClick={() => { onReact(m.id, e); setShowReactionPicker(false); }} 
                  className="text-lg sm:text-xl hover:scale-125 transition-transform"
                >
                  {e}
                </button>
              ))}
              <button onClick={() => setShowReactionPicker(false)} className="text-white/50 hover:text-white ml-1 text-xs font-bold">✕</button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={`flex items-center gap-1.5 mt-1.5 flex-wrap ${mine ? "justify-end" : "justify-start"}`}>
          {reactions.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {reactions.map(([e, us]) => (
                <button key={e} onClick={() => onReact(m.id, e)} data-testid={`reaction-${m.id}-${e}`}
                  className={`text-xs sm:text-sm px-2.5 py-1 rounded-full transition-colors flex items-center gap-1.5 font-medium ${us.includes(me?.id) ? "bg-white/10 border-white/20" : "bg-white/5 text-white/70 border border-white/10 hover:bg-white/10"}`}>
                  <span className="text-sm sm:text-base">{e}</span>
                  <span className="text-[11px] sm:text-xs font-semibold">{us.length}</span>
                </button>
              ))}
            </div>
          )}

          <div className="relative flex items-center">
            <button onClick={() => setShowReactionPicker((v) => !v)} className="p-1 rounded-full hover:bg-white/10 text-white/40 hover:text-white transition-colors text-[10px]">
              React
            </button>
            <button onClick={() => setMenu((v) => !v)} data-testid={`msg-menu-${m.id}`} className="p-1 rounded-full hover:bg-white/10 text-white/50 hover:text-white transition-colors">
              <MoreVertical className="w-3.5 h-3.5" />
            </button>
            <AnimatePresence>
              {menu && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className={`absolute z-[999] top-full mt-1 w-44 glass-strong rounded-xl border border-white/10 p-1.5 text-xs shadow-2xl ${mine ? "right-0" : "left-0"}`} 
                  onMouseLeave={() => setMenu(false)}
                >
                  <div className="grid grid-cols-5 gap-1 p-1 mb-1 border-b border-white/5">
                    {EMOJIS.slice(0, 5).map((e) => (
                      <button key={e} onClick={() => { onReact(m.id, e); setMenu(false); }} className="text-base hover:scale-125 transition-transform py-0.5">{e}</button>
                    ))}
                  </div>
                  <button onClick={() => { onReply(m); setMenu(false); }} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white/10 text-white/80"><Reply className="w-3.5 h-3.5" /> Reply</button>
                  {mine && m.text && !m.shared && <button onClick={() => { setDraft(m.text || ""); setEditing(true); setMenu(false); }} data-testid={`msg-edit-${m.id}`} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white/10 text-white/80"><Pencil className="w-3.5 h-3.5" /> Edit</button>}
                  {canPin && <button onClick={() => { onPin(m); setMenu(false); }} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white/10 text-white/80"><Pin className="w-3.5 h-3.5" /> {m.pinned ? "Unpin" : "Pin"}</button>}
                  {canDelete && <button onClick={() => { onDelete(m); setMenu(false); }} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white/10 text-red-400"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showReactionDetails && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 360, damping: 28 }}
            className="absolute z-[1000] left-1/2 -translate-x-1/2 bottom-10 w-[min(320px,calc(100vw-32px))] glass-strong rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="min-w-0">
                <div className="font-semibold text-sm text-white">
                  Reactions
                </div>
                <div className="text-[11px] text-white/45 truncate">
                  Who reacted to this message
                </div>
              </div>

              <button
                onClick={() => setShowReactionDetails(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                aria-label="Close reactions"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-72 overflow-y-auto p-2.5">
              {loadingReactionDetails ? (
                <div className="py-8 flex flex-col items-center justify-center gap-2 text-white/45">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-xs">Loading reactions...</span>
                </div>
              ) : !reactionDetails || Object.keys(reactionDetails).length === 0 ? (
                <div className="py-8 text-center text-xs text-white/40">
                  No reactions yet
                </div>
              ) : (
                Object.entries(reactionDetails).map(([emoji, users]) => (
                  <div
                    key={emoji}
                    className="rounded-xl bg-white/[0.035] border border-white/5 p-2.5 mb-2 last:mb-0"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{emoji}</span>
                      <span className="text-xs font-semibold text-white/70">
                        {users.length} {users.length === 1 ? "reaction" : "reactions"}
                      </span>
                    </div>

                    <div className="space-y-1">
                      {users.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-white/5 transition-colors"
                        >
                          <img
                            src={u.avatar_url || undefined}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover bg-white/10 border border-white/10 shrink-0"
                          />

                          <div className="min-w-0">
                            <div className="text-xs font-medium text-white truncate">
                              {u.name || u.username || "User"}
                            </div>

                            {u.username && (
                              <div className="text-[10px] text-white/40 truncate">
                                @{u.username}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="px-3 py-2 border-t border-white/5 text-[10px] text-white/30 text-center">
              Swipe left again to view reactions
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
}

export default function CommunityPage() {
  const { cid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [community, setCommunity] = useState(null);
  const [communityId, setCommunityId] = useState(cid);
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [loading, setLoading] = useState(true);

  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [videoPopup, setVideoPopup] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimerRef = useRef(null);
  const oldestMessageRef = useRef(null);

  const [typingUsers, setTypingUsers] = useState({});

  const perms = community?.my_perms || [];
  const canSend = perms.includes("SEND_MESSAGES");

  // Community appearance
  const communityAppearance = community?.appearance || {};
  const communityChatAppearance = communityAppearance.chat || {};

  const chatBackground =
    communityChatAppearance.background_type === "solid"
      ? communityChatAppearance.background_color || "#0B0B12"
      : `linear-gradient(135deg, ${
          communityChatAppearance.gradient_from || "#18102F"
        }, ${
          communityChatAppearance.gradient_to || "#09090B"
        })`;

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);

    try {
      const c = await api.get(`/communities/${cid}`);
      setCommunity(c.data);

      // URL can be a slug, but internal APIs continue using the UUID.
      const resolvedCommunityId = c.data.id || cid;
      setCommunityId(resolvedCommunityId);

      if (c.data.is_member) {
        const [msgs, pins] = await Promise.all([
          api.get(`/communities/${resolvedCommunityId}/messages`, {
            params: { limit: PAGE_SIZE },
          }),
          api.get(`/communities/${resolvedCommunityId}/pinned`),
        ]);

        const loadedMessages = msgs.data?.messages || [];

        setMessages(loadedMessages);
        setPinned(pins.data);

        setHasMoreMessages(msgs.data?.has_more ?? false);
        oldestMessageRef.current = loadedMessages[0] || null;

        scrollToBottom();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [cid, communityId, scrollToBottom]);

  const loadOlderMessages = useCallback(async () => {
    if (loadingOlderMessages || !hasMoreMessages) return;

    const oldest = oldestMessageRef.current;

    if (!oldest?.created_at) return;

    const container = scrollRef.current;

    if (!container) return;

    setLoadingOlderMessages(true);

    // Save the current scroll position before adding older messages.
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    try {
      const res = await api.get(`/communities/${communityId}/messages`, {
        params: {
          limit: PAGE_SIZE,
          before: oldest.created_at,
        },
      });

      const olderMessages = res.data?.messages || [];

      if (olderMessages.length > 0) {
        oldestMessageRef.current = olderMessages[0];

        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));

          const uniqueOlder = olderMessages.filter(
            (m) => !existingIds.has(m.id)
          );

          return [...uniqueOlder, ...prev];
        });
      }

      setHasMoreMessages(res.data?.has_more ?? false);

      // Restore the viewport so the screen doesn't jump.
      requestAnimationFrame(() => {
        if (!scrollRef.current) return;

        const newScrollHeight = scrollRef.current.scrollHeight;

        scrollRef.current.scrollTop =
          previousScrollTop +
          (newScrollHeight - previousScrollHeight);
      });
    } catch (e) {
      console.error("Failed to load older messages:", e);
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [
    cid,
    hasMoreMessages,
    loadingOlderMessages,
  ]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const applyEvent = useCallback((data) => {
    if (data.type === "message") {
      setMessages((prev) => prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]);
      scrollToBottom();
    } else if (data.type === "edit") {
      setMessages((prev) => prev.map((m) => m.id === data.message_id ? { ...m, text: data.text, edited: true, edited_at: data.edited_at } : m));
    } else if (data.type === "delete") {
      setMessages((prev) => prev.filter((m) => m.id !== data.message_id));
    } else if (data.type === "react") {
      setMessages((prev) => prev.map((m) => m.id === data.message_id ? { ...m, reactions: data.reactions } : m));
    } else if (data.type === "pin") {
      api.get(`/communities/${communityId}/pinned`).then((r) => setPinned(r.data)).catch(() => {});
    } else if (data.type === "typing") {
      if (!data.user_id || data.user_id === user?.id) return;
      setTypingUsers((prev) => {
        const next = { ...prev };
        if (data.action === "start") next[data.user_id] = data.username || "Someone";
        else delete next[data.user_id];
        return next;
      });
    } else if (data.type === "settings") {
      setCommunity((c) => ({ ...c, admin_only: data.admin_only }));
    }
  }, [communityId, scrollToBottom, user?.id]);

  // Real-time: WebSocket with auto-reconnect + polling fallback so new messages always appear without reload.
  const lastSyncRef = useRef(null);
  useEffect(() => {
    if (!community?.is_member || !communityId) return;
    let closed = false;
    let ws;
    let retry = 0;
    let reconnectTimer;
    lastSyncRef.current = new Date().toISOString();

    const connect = async () => {
      if (closed) return;
      let token;
      try {
        token = await getCreatorCommunityToken();
      } catch {
        scheduleReconnect();
        return;
      }
      if (closed) return;
      if (!token) {
        // No Store login: public community browsing still works, but live chat needs login.
        return;
      }
      const wsUrl = BACKEND.replace(/^http/, "ws") + `/creator-api/ws/community/${communityId}?token=${encodeURIComponent(token)}`;
      try { ws = new WebSocket(wsUrl); } catch { scheduleReconnect(); return; }
      wsRef.current = ws;
      ws.onopen = () => { retry = 0; };
      ws.onmessage = (ev) => { try { applyEvent(JSON.parse(ev.data)); } catch {} };
      ws.onclose = async (ev) => {
        if (closed) return;
        // Expired token → silently refresh the session, then reconnect.
        if (ev.code === 4401) { try { await authClient.get("/auth/me"); } catch {} }
        scheduleReconnect();
      };
    };
    const scheduleReconnect = () => {
      if (closed) return;
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, Math.min(10000, 1000 * 2 ** retry++));
    };

    const poll = async () => {
      if (closed || document.hidden) return;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
      try {
        const { data } = await api.get(`/communities/${communityId}/messages/since`, { params: { after: lastSyncRef.current } });
        lastSyncRef.current = data.server_time || lastSyncRef.current;
        (data.messages || []).forEach((m) => {
          if (m.deleted) { applyEvent({ type: "delete", message_id: m.id }); return; }
          setMessages((prev) => {
            const idx = prev.findIndex((x) => x.id === m.id);
            if (idx === -1) return [...prev, m];
            const next = [...prev];
            next[idx] = { ...next[idx], ...m };
            return next;
          });
        });
        if ((data.messages || []).some((m) => !m.deleted)) scrollToBottom();
      } catch {}
    };

    connect();
    const pollTimer = setInterval(poll, 3000);
    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      clearInterval(pollTimer);
      clearTimeout(typingTimerRef.current);
      try { ws && ws.send(JSON.stringify({ type: "typing", action: "stop" })); } catch {}
      try { ws && ws.close(); } catch {}
    };
  }, [community?.is_member, communityId, applyEvent, scrollToBottom]);

  const join = async () => {
    try {
      const { data } = await api.post(`/communities/${communityId}/join`);
      setCommunity(data);
      if (data.requested) { toast.success("Request sent — an admin will review it"); }
      else { loadAll(); toast.success("Joined!"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not join"); }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const form = new FormData();
      form.append("file", f); form.append("kind", "auto");
      try {
        const { data } = await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, data]);
      } catch (err) { toast.error(err.response?.data?.detail || `Upload failed: ${f.name}`); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const sendTyping = useCallback((isTyping) => {
    const ws = wsRef.current;

    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    try {
      ws.send(JSON.stringify({
        type: "typing",
        action: isTyping ? "start" : "stop",
      }));
    } catch {}
  }, []);

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);

    if (!value.trim()) {
      clearTimeout(typingTimerRef.current);
      sendTyping(false);
      return;
    }

    sendTyping(true);

    clearTimeout(typingTimerRef.current);

    typingTimerRef.current = setTimeout(() => {
      sendTyping(false);
    }, 1800);
  };

  const send = async () => {
    if (!text.trim() && !attachments.length) return;
    setSending(true);
    try {
      const { data } = await api.post(`/communities/${communityId}/messages`, {
        text: text.trim(),
        media: attachments,
        reply_to: replyTo?.id || null,
      });
      setMessages((prev) => prev.some((m) => m.id === data.id) ? prev : [...prev, data]);
      setText("");
      setAttachments([]);
      setReplyTo(null);
      setShowEmoji(false);
      clearTimeout(typingTimerRef.current);
      sendTyping(false);
      scrollToBottom();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  const doPin = async (m) => { try { await api.post(`/communities/${communityId}/messages/${m.id}/pin`); } catch { toast.error("Failed"); } };
  const doEdit = async (m, newText) => {
    try {
      const { data } = await api.patch(`/communities/${communityId}/messages/${m.id}`, { text: newText });
      setMessages((p) => p.map((x) => x.id === m.id ? { ...x, text: data.text, edited: true, edited_at: data.edited_at } : x));
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not edit"); return false; }
  };
  const doDelete = async (m) => {
    try { await api.delete(`/communities/${communityId}/messages/${m.id}`); setMessages((p) => p.filter((x) => x.id !== m.id)); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doReact = async (mid, emoji) => {
    try { const { data } = await api.post(`/communities/${communityId}/messages/${mid}/react`, { emoji }); setMessages((p) => p.map((m) => m.id === mid ? { ...m, reactions: data.reactions } : m)); }
    catch {}
  };

  if (loading) return <div className="flex justify-center h-screen items-center"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>;
  if (!community) return <div className="max-w-lg mx-auto py-24 text-center text-white/50">Community not found.</div>;

  return (
    <div className="fixed inset-x-0 bottom-0 top-[60px] sm:top-[64px] sm:inset-auto sm:relative sm:w-full sm:max-w-5xl sm:mx-auto sm:my-4 sm:rounded-2xl sm:h-[calc(100vh-80px)] flex flex-col bg-[#0b0b12] overflow-hidden select-none sm:border sm:border-white/10 sm:shadow-2xl" data-testid="community-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-white/10 glass shrink-0 z-20 sm:rounded-t-2xl">
        <button onClick={() => navigate(communitiesBase())} className="p-2 rounded-full glass-hover"><ArrowLeft className="w-4 h-4" /></button>
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden bg-[#15121f] flex items-center justify-center shrink-0">
          {community.icon_url ? <img src={community.icon_url} alt="" className="w-full h-full object-cover" /> : <Users className="w-5 h-5 text-white/50" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm sm:text-base truncate flex items-center gap-2">
            {community.name}
            {community.admin_only && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] shrink-0"><Lock className="w-3 h-3" /> Admin only</span>}
          </div>
          <div className="text-[11px] sm:text-xs text-white/45">{community.member_count} members</div>
        </div>
        <button onClick={() => setShowMembers(true)} data-testid="community-members-btn" className="relative p-2 rounded-full glass-hover shrink-0">
          <Users className="w-4 h-4" />
          {community.request_count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF6AD5] text-black text-[10px] font-bold flex items-center justify-center" data-testid="request-badge">{community.request_count}</span>
          )}
        </button>
        {perms.includes("MANAGE_COMMUNITY") && (
          <button onClick={() => setShowSettings(true)} data-testid="community-settings-btn" className="p-2 rounded-full glass-hover shrink-0"><Settings className="w-4 h-4" /></button>
        )}
      </div>

      {/* Pinned Bar */}
      {pinned.length > 0 && (
        <div className="px-4 py-1.5 border-b border-white/10 bg-white/[0.03] flex items-center gap-2 text-xs shrink-0" data-testid="pinned-bar">
          <Pin className="w-3.5 h-3.5 text-[#FFD54A] shrink-0" />
          <span className="text-white/60 truncate">{pinned[0].text || `${pinned[0].media?.length || 0} attachment(s)`}</span>
          {pinned.length > 1 && <span className="text-white/40 shrink-0">+{pinned.length - 1}</span>}
        </div>
      )}

      {!community.is_member ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center overflow-y-auto">
          {community.banner_url && <img src={community.banner_url} alt="" className="w-full max-w-md h-32 object-cover rounded-2xl border border-white/10" />}
          <Users className="w-10 h-10 text-white/30" />
          <div className="font-display text-xl font-bold flex items-center gap-2">
            {community.name}
            {community.is_public === false && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs"><Lock className="w-3 h-3" /> Private</span>}
          </div>
          <p className="text-white/50 max-w-sm text-sm">{community.description}</p>
          {community.has_requested ? (
            <Button disabled data-testid="community-request-pending" className="rounded-full bg-white/10 text-white/75">
              Request pending
            </Button>
          ) : (
            <Button onClick={join} data-testid="community-join-btn" className="rounded-full text-black font-semibold" style={{ background: "linear-gradient(135deg,#9387FE,#FF6AD5)" }}>
              {community.is_public === false ? "Request to Join" : "Join Community"}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 relative">
          <div
            ref={scrollRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.scrollTop <= 100) {
                loadOlderMessages();
              }
            }}
            className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4"
            style={{ background: chatBackground }}
            data-testid="messages-list"
          >
            {loadingOlderMessages && (
              <div className="flex justify-center py-2">
                <div className="text-xs text-white/40">
                  Loading older messages...
                </div>
              </div>
            )}

            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-white/40">
                <Send className="w-8 h-8 mb-2" /><div className="font-medium">No messages yet</div>
                <div className="text-sm">{canSend ? "Say hello 👋" : "Only admins can post here."}</div>
              </div>
            ) : messages.map((m, i) => (
              <React.Fragment key={m.id}>
                <MessageBubble m={m} me={user} perms={perms}
                  accentColor={communityAppearance.accent_color || "#FFFFFF"}
                  communityId={communityId}
                  replyTo={replyTo}
                  onReply={setReplyTo} onPin={doPin} onDelete={doDelete} onReact={doReact} onEdit={doEdit}
                  onImage={(imgs, idx) => setLightbox({ items: imgs.map((x) => ({ ...x, url: abs(x.url) })), index: idx })}
                  onVideo={(v) => setVideoPopup({ src: abs(v.url), title: v.name })} />
                {(i + 1) % 8 === 0 && i < messages.length - 1 && (
                  <div className="community-chat-ad">
                    <PluseAdSense type="inFeed" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Composer Footer */}
          {Object.keys(typingUsers).length > 0 && (
            <div className="px-4 pb-1.5 text-[11px] text-white/45 flex items-center gap-2 shrink-0">
              <span>
                {(() => {
                  const names = Object.values(typingUsers);
                  if (names.length === 1) return `${names[0]} is typing`;
                  if (names.length === 2) return `${names[0]} and ${names[1]} are typing`;
                  return `${names.length} people are typing`;
                })()}
              </span>
              <span className="flex items-center gap-0.5">
                <span className="w-1 h-1 rounded-full bg-white/40 animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1 h-1 rounded-full bg-white/40 animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1 h-1 rounded-full bg-white/40 animate-bounce" />
              </span>
            </div>
          )}

          {canSend ? (
            <div className="shrink-0 relative z-30 px-2.5 pb-2.5 pt-1.5 sm:px-4 sm:pb-4 sm:pt-2">
              {replyTo && (
                <div
                  className="mb-2 px-3 py-2 rounded-2xl border border-white/10 bg-white/[0.045] backdrop-blur-xl flex items-center gap-2"
                  style={{ borderLeftColor: communityAppearance.accent_color || "#FFFFFF", borderLeftWidth: 3 }}
                >
                  <Reply
                    className="w-3.5 h-3.5 shrink-0"
                    style={{ color: communityAppearance.accent_color || "#FFFFFF" }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wide text-white/35">Replying to</div>
                    <div className="text-xs text-white/75 truncate">
                      <b className="text-white">@{replyTo.author?.username || "user"}</b>
                      {" · "}
                      {replyTo.text || "Attachment"}
                    </div>
                  </div>
                  <button
                    onClick={() => setReplyTo(null)}
                    className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-colors shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {attachments.length > 0 && (
                <div
                  className="flex gap-2 mb-2 overflow-x-auto pb-0.5"
                  data-testid="attachment-previews"
                >
                  {attachments.map((a, i) => (
                    <div
                      key={i}
                      className="relative shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden border border-white/10 bg-black/30 flex items-center justify-center shadow-lg"
                    >
                      {a.kind === "image" ? (
                        <img src={abs(a.url)} alt="" className="w-full h-full object-cover" />
                      ) : a.kind === "video" ? (
                        <VideoIcon className="w-6 h-6 text-white/50" />
                      ) : (
                        <Paperclip className="w-6 h-6 text-white/50" />
                      )}

                      <button
                        onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/75 backdrop-blur flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showEmoji && (
                <div className="absolute bottom-full left-2.5 sm:left-4 mb-2 glass-strong rounded-2xl border border-white/10 p-2.5 flex flex-wrap gap-1.5 w-[min(280px,calc(100vw-20px))] z-50 shadow-2xl">
                  {EMOJIS.map((e) => (
                    <button
                      key={e}
                      onClick={() => {
                        setText((t) => t + e);
                        setShowEmoji(false);
                      }}
                      className="text-xl hover:scale-125 transition-transform p-1.5 rounded-lg hover:bg-white/5"
                    >
                      {e}
                    </button>
                  ))}
                </div>
              )}

              <div
                className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-[26px] border border-white/10 bg-white/[0.045] backdrop-blur-2xl shadow-[0_8px_30px_rgba(0,0,0,0.25)]"
                style={{
                  boxShadow: `0 8px 30px rgba(0,0,0,0.25), 0 0 0 1px ${communityAppearance.accent_color || "#FFFFFF"}0A`
                }}
              >
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.zip,.txt"
                  onChange={onFiles}
                  className="hidden"
                  data-testid="community-file-input"
                />

                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 active:scale-95 transition-all shrink-0"
                  data-testid="composer-attach-btn"
                  title="Attach"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Paperclip className="w-4 h-4 text-white/65" />
                  )}
                </button>

                <button
                  onClick={() => setShowEmoji((v) => !v)}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 active:scale-95 transition-all shrink-0"
                  title="Emoji"
                >
                  <Smile className="w-4 h-4 text-white/65" />
                </button>

                <Input
                  value={text}
                  onChange={handleTextChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Write a message…"
                  data-testid="community-composer-input"
                  className="flex-1 min-w-0 bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 h-10 text-[13px] sm:text-sm placeholder:text-white/30"
                />

                <Button
                  onClick={send}
                  disabled={sending || (!text.trim() && !attachments.length)}
                  data-testid="community-send-btn"
                  className="w-10 h-10 rounded-full text-black shrink-0 p-0 flex items-center justify-center transition-all active:scale-90 disabled:opacity-30 disabled:scale-100"
                  style={{
                    background: `linear-gradient(135deg, ${communityAppearance.accent_color || "#FFFFFF"}, ${communityAppearance.accent_color || "#FFFFFF"}B8)`
                  }}
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="border-t border-white/10 p-4 text-center text-sm text-white/40 glass shrink-0"
              data-testid="readonly-notice"
            >
              <Lock className="w-4 h-4 inline mr-1.5" />
              Admin-only mode is on. Only admins can post.
            </div>
          )}
        </div>
      )}

      {lightbox && <MediaLightbox items={lightbox.items} index={lightbox.index} onClose={() => setLightbox(null)} />}
      {videoPopup && <VideoPopup src={videoPopup.src} title={videoPopup.title} onClose={() => setVideoPopup(null)} />}
      <AnimatePresence>
        {showMembers && <MembersDrawer communityId={communityId} community={community} onClose={() => setShowMembers(false)} onChanged={loadAll} />}
      </AnimatePresence>
      <AnimatePresence>
        {showSettings && <SettingsModal community={community} onClose={() => setShowSettings(false)} onChanged={(c) => setCommunity((prev) => ({ ...prev, ...c }))} onDeleted={() => navigate(communitiesBase())} />}
      </AnimatePresence>
    </div>
  );
}

function MembersDrawer({ communityId, community, onClose, onChanged }) {
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const perms = community.my_perms || [];
  const canManage = perms.includes("MANAGE_MEMBERS");
  const isPrivileged = perms.includes("MANAGE_COMMUNITY") || perms.includes("MANAGE_MEMBERS");
  
  const load = useCallback(() => {
    setLoading(true);
    const jobs = [api.get(`/communities/${communityId}/members`).then((r) => setMembers(r.data)).catch(() => {})];
    if (canManage) jobs.push(api.get(`/communities/${communityId}/requests`).then((r) => setRequests(r.data)).catch(() => {}));
    Promise.all(jobs).finally(() => setLoading(false));
  }, [communityId, canManage]);

  useEffect(() => { load(); }, [load]);

  const act = async (uid, fn) => { try { await fn(); load(); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const setRole = (uid, role) => act(uid, () => api.post(`/communities/${communityId}/members/${uid}/role`, { role }));
  const remove = (uid) => act(uid, () => api.delete(`/communities/${communityId}/members/${uid}`));
  const approve = (uid) => act(uid, () => api.post(`/communities/${communityId}/requests/${uid}/approve`));
  const reject = (uid) => act(uid, () => api.post(`/communities/${communityId}/requests/${uid}/reject`));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black/65 backdrop-blur-sm flex items-end sm:items-stretch sm:justify-end"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ y: 80, x: 0 }}
        animate={{ y: 0, x: 0 }}
        exit={{ y: 80, x: 0 }}
        transition={{ type: "spring", damping: 28, stiffness: 320 }}
        className="w-full sm:w-96 max-w-full h-[88vh] sm:h-full rounded-t-3xl sm:rounded-none glass-strong border-t sm:border-t-0 sm:border-l border-white/10 overflow-hidden flex flex-col shadow-2xl"
        data-testid="members-drawer"
      >
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-2 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        <div className="flex items-center justify-between px-4 pt-3 pb-3 sm:p-4 border-b border-white/10 shrink-0">
          <div>
            <h3 className="font-display font-bold text-lg">
              {isPrivileged ? "Members" : "Admins & Owners"}
            </h3>
            <div className="text-xs text-white/40 mt-0.5">
              {members.length} member{members.length === 1 ? "" : "s"}
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            aria-label="Close members"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
        {!isPrivileged && (
          <p className="text-xs text-white/40 mb-4 px-1">
            For privacy, only community admins and owners are visible to regular members.
          </p>
        )}
        {canManage && requests.length > 0 && (
          <div className="mb-4" data-testid="join-requests">
            <div className="text-xs uppercase tracking-widest text-[#FF6AD5] mb-2">Join requests · {requests.length}</div>
            {requests.map((r) => (
              <div key={r.id} className="flex items-center gap-2 py-2 border-b border-white/5" data-testid={`join-request-${r.id}`}>
                <img src={r.avatar_url || undefined} alt="" className="w-9 h-9 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-xs text-white/40">@{r.username}</div>
                </div>
                <button onClick={() => approve(r.id)} data-testid={`approve-request-${r.id}`} className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25">Approve</button>
                <button onClick={() => reject(r.id)} data-testid={`reject-request-${r.id}`} className="text-[10px] px-2 py-1 rounded-full bg-red-500/10 text-red-300 hover:bg-red-500/20">Reject</button>
              </div>
            ))}
          </div>
        )}
        {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-white/40" /> : members.map((m) => (
          <div key={m.id} className="flex items-center gap-2 py-2 border-b border-white/5" data-testid={`member-${m.id}`}>
            <img src={m.avatar_url || undefined} alt="" className="w-9 h-9 rounded-full object-cover" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate flex items-center gap-1">
                {m.name}
                {m.role === "OWNER" && <Crown className="w-3.5 h-3.5 text-[#FFD54A]" />}
                {m.role === "ADMIN" && <Shield className="w-3.5 h-3.5 text-[#9387FE]" />}
              </div>
              <div className="text-xs text-white/40">@{m.username} {m.banned && <span className="text-red-400">· banned</span>}</div>
            </div>
            {m.role !== "OWNER" && canManage && (
              <div className="flex gap-1">
                {perms.includes("MANAGE_ADMINS") && (m.role === "ADMIN"
                  ? <button onClick={() => setRole(m.id, "MEMBER")} title="Demote" className="text-[10px] px-2 py-1 rounded-full bg-white/5 hover:bg-white/10">Demote</button>
                  : <button onClick={() => setRole(m.id, "ADMIN")} title="Promote" className="text-[10px] px-2 py-1 rounded-full bg-white/5 hover:bg-white/10">Promote</button>)}
                <button onClick={() => remove(m.id)} title="Remove" className="text-[10px] px-2 py-1 rounded-full bg-red-500/10 text-red-300 hover:bg-red-500/20">Kick</button>
              </div>
            )}
          </div>
        ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

function SettingsModal({ community, onClose, onChanged, onDeleted }) {
  const perms = community.my_perms || [];
  const [name, setName] = useState(community.name);
  const [description, setDescription] = useState(community.description || "");
  const [iconUrl, setIconUrl] = useState(community.icon_url || "");
  const [bannerUrl, setBannerUrl] = useState(community.banner_url || "");
  const [isPublic, setIsPublic] = useState(community.is_public !== false);
  const [adminOnly, setAdminOnly] = useState(community.admin_only);
  const [saving, setSaving] = useState(false);
  const communityId = community.id;

  const appearance = community.appearance || {};
  const cardAppearance = appearance.card || {};
  const chatAppearance = appearance.chat || {};

  const [accentColor, setAccentColor] = useState(
    appearance.accent_color || "#FFFFFF"
  );

  const [cardGradientEnabled, setCardGradientEnabled] = useState(
    cardAppearance.gradient_enabled === true
  );

  const [cardGradientFrom, setCardGradientFrom] = useState(
    cardAppearance.gradient_from || "#2A2A2A"
  );

  const [cardGradientTo, setCardGradientTo] = useState(
    cardAppearance.gradient_to || "#111111"
  );

  const [cardGradientDirection, setCardGradientDirection] = useState(
    cardAppearance.gradient_direction || "135deg"
  );

  const [chatBackgroundType, setChatBackgroundType] = useState(
    chatAppearance.background_type || "solid"
  );

  const [chatGradientFrom, setChatGradientFrom] = useState(
    chatAppearance.gradient_from || "#18102F"
  );

  const [chatGradientTo, setChatGradientTo] = useState(
    chatAppearance.gradient_to || "#09090B"
  );

  const [chatBackgroundColor, setChatBackgroundColor] = useState(
    chatAppearance.background_color || "#0B0B12"
  );

  const save = async () => {
    setSaving(true);
    try {
      const appearanceData = {
        accent_color: accentColor,
        card: {
          gradient_enabled: cardGradientEnabled,
          gradient_from: cardGradientFrom,
          gradient_to: cardGradientTo,
          gradient_direction: cardGradientDirection,
        },
        chat: {
          background_type: chatBackgroundType,
          background_color: chatBackgroundColor,
          gradient_from: chatGradientFrom,
          gradient_to: chatGradientTo,
        },
      };

      const { data } = await api.put(`/communities/${communityId}`, {
        name,
        description,
        icon_url: iconUrl,
        banner_url: bannerUrl,
        is_public: isPublic,
        appearance: appearanceData,
      });
      onChanged?.(data); toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  const toggleAdminOnly = async () => {
    try { const { data } = await api.post(`/communities/${communityId}/admin-only`, { enabled: !adminOnly }); setAdminOnly(data.admin_only); onChanged?.({ admin_only: data.admin_only }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this community permanently?")) return;
    try {
      await api.delete(`/communities/${communityId}`);
      toast.success("Deleted");
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete community");
      console.error("Delete community failed:", e.response?.data || e);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} className="glass-strong w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 border border-white/10 max-h-[90vh] overflow-y-auto" data-testid="community-settings-modal">
        <div className="flex items-center justify-between mb-4"><h3 className="font-display text-xl font-bold">Community Settings</h3><button onClick={onClose} className="p-2 rounded-full glass-hover"><X className="w-4 h-4" /></button></div>
        <div className="space-y-4">
          <div>
            <div className="text-xs text-white/50 mb-1.5">Banner</div>
            <div className="h-24 rounded-2xl overflow-hidden bg-white/5 border border-white/10 mb-2 flex items-center justify-center">
              {bannerUrl ? <img src={bannerUrl.startsWith("http") ? bannerUrl : `${BACKEND}${bannerUrl}`} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-white/30">No banner</span>}
            </div>
            <FileUpload kind="image" onUploaded={(d) => setBannerUrl(d?.url || "")} buttonLabel="Change banner" testid="settings-banner-upload" />
          </div>
          <div>
            <div className="text-xs text-white/50 mb-1.5">Icon</div>
            <FileUpload kind="image" onUploaded={(d) => setIconUrl(d?.url || "")} buttonLabel="Change icon" testid="settings-icon-upload" />
          </div>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="settings-name" />
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" className="bg-white/5 border-white/10" data-testid="settings-desc" />
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
            <div><div className="text-sm font-medium flex items-center gap-1.5"><Lock className="w-4 h-4" /> Private group</div><div className="text-xs text-white/40">Members join by request</div></div>
            <button onClick={() => setIsPublic((v) => !v)} data-testid="settings-privacy-toggle"
              className={`w-12 h-7 rounded-full p-1 transition-colors ${!isPublic ? "bg-amber-400" : "bg-white/10"}`}>
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${!isPublic ? "translate-x-5" : ""}`} />
            </button>
          </div>
          {perms.includes("TOGGLE_ADMIN_ONLY") && (
            <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
              <div><div className="text-sm font-medium flex items-center gap-1.5"><Lock className="w-4 h-4" /> Admin-only mode</div><div className="text-xs text-white/40">Only admins can post</div></div>
              <button onClick={toggleAdminOnly} data-testid="settings-admin-only-toggle"
                className={`w-12 h-7 rounded-full p-1 transition-colors ${adminOnly ? "bg-[#9387FE]" : "bg-white/10"}`}>
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${adminOnly ? "translate-x-5" : ""}`} />
              </button>
            </div>
          )}
          {/* Appearance */}
          <div className="pt-4 mt-4 border-t border-white/10">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FF6AD5]" />
              Appearance
            </div>

            {/* Accent Color */}
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 mb-3">
              <div className="text-xs text-white/50 mb-2">Accent Color</div>

              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-10 h-10 rounded-lg bg-transparent border-0 cursor-pointer"
                />

                <Input
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="bg-white/5 border-white/10 font-mono text-sm"
                  maxLength={7}
                />
              </div>
            </div>

            {/* Community Card Gradient */}
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 mb-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium">Community Card Gradient</div>
                  <div className="text-xs text-white/40">
                    Customize the gradient shown on community cards.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setCardGradientEnabled(!cardGradientEnabled)}
                  className={`w-12 h-7 rounded-full p-1 transition-colors ${
                    cardGradientEnabled ? "bg-[#9387FE]" : "bg-white/10"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      cardGradientEnabled ? "translate-x-5" : ""
                    }`}
                  />
                </button>
              </div>

              {cardGradientEnabled && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-white/40 mb-1">Start</div>
                      <input
                        type="color"
                        value={cardGradientFrom}
                        onChange={(e) => setCardGradientFrom(e.target.value)}
                        className="w-full h-10 rounded-lg cursor-pointer bg-transparent"
                      />
                    </div>

                    <div>
                      <div className="text-xs text-white/40 mb-1">End</div>
                      <input
                        type="color"
                        value={cardGradientTo}
                        onChange={(e) => setCardGradientTo(e.target.value)}
                        className="w-full h-10 rounded-lg cursor-pointer bg-transparent"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="text-xs text-white/40 mb-1">Direction</div>
                    <select
                      value={cardGradientDirection}
                      onChange={(e) => setCardGradientDirection(e.target.value)}
                      className="w-full rounded-xl bg-[#15151f] border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-white/20"
                    >
                      <option value="90deg" className="bg-[#15151f] text-white">Left → Right</option>
                      <option value="135deg" className="bg-[#15151f] text-white">Diagonal ↘</option>
                      <option value="180deg" className="bg-[#15151f] text-white">Top → Bottom</option>
                      <option value="45deg" className="bg-[#15151f] text-white">Diagonal ↗</option>
                      <option value="270deg" className="bg-[#15151f] text-white">Right → Left</option>
                    </select>
                  </div>

                  <div
                    className="mt-3 h-16 rounded-xl border border-white/10"
                    style={{
                      background: `linear-gradient(${cardGradientDirection}, ${cardGradientFrom}, ${cardGradientTo})`
                    }}
                  />
                </>
              )}
            </div>

            {/* Chat Background */}
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="text-sm font-medium mb-1">Chat Background</div>
              <div className="text-xs text-white/40 mb-3">
                Customize the background of the community chat.
              </div>

              <select
                value={chatBackgroundType}
                onChange={(e) => setChatBackgroundType(e.target.value)}
                className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-white mb-3"
              >
                <option value="gradient">Gradient</option>
                <option value="solid">Solid Color</option>
              </select>

              {chatBackgroundType === "solid" ? (
                <div>
                  <div className="text-xs text-white/40 mb-1">Background Color</div>
                  <input
                    type="color"
                    value={chatBackgroundColor}
                    onChange={(e) => setChatBackgroundColor(e.target.value)}
                    className="w-full h-10 rounded-lg cursor-pointer bg-transparent"
                  />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-white/40 mb-1">Start</div>
                      <input
                        type="color"
                        value={chatGradientFrom}
                        onChange={(e) => setChatGradientFrom(e.target.value)}
                        className="w-full h-10 rounded-lg cursor-pointer bg-transparent"
                      />
                    </div>

                    <div>
                      <div className="text-xs text-white/40 mb-1">End</div>
                      <input
                        type="color"
                        value={chatGradientTo}
                        onChange={(e) => setChatGradientTo(e.target.value)}
                        className="w-full h-10 rounded-lg cursor-pointer bg-transparent"
                      />
                    </div>
                  </div>

                  <div
                    className="mt-3 h-16 rounded-xl border border-white/10"
                    style={{
                      background: `linear-gradient(135deg, ${chatGradientFrom}, ${chatGradientTo})`
                    }}
                  />
                </>
              )}
            </div>
          </div>

          <Button onClick={save} disabled={saving} data-testid="settings-save" className="w-full rounded-full text-black font-semibold" style={{ background: "linear-gradient(135deg,#9387FE,#FF6AD5)" }}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}</Button>

        {perms.includes("DELETE_COMMUNITY") && (
          <Button onClick={del} variant="ghost" data-testid="settings-delete" className="w-full rounded-full text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4 mr-1.5" /> Delete Community</Button>
        )}
        </div>
      </motion.div>
    </motion.div>
  );
}